import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useProfile } from "@/hooks/useProfile";
import Navbar from "@/components/Navbar";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { EditBasicInfo } from "@/components/kb-edit/EditBasicInfo";
import { EditDetails } from "@/components/kb-edit/EditDetails";
import { EditDocuments } from "@/components/kb-edit/EditDocuments";
import { EditConfirmation } from "@/components/kb-edit/EditConfirmation";

type EditStep = 1 | 2 | 3 | 4;

const EditKnowledgeBase = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useProfile();
  const [currentStep, setCurrentStep] = useState<EditStep>(1);
  const [kbData, setKbData] = useState<any>(null);

  // Fetch existing KB data
  const { data: knowledgeBase, isLoading, error } = useQuery({
    queryKey: ['knowledge-base', id],
    queryFn: async () => {
      if (!id) throw new Error('No ID provided');
      
      const { data, error } = await supabase
        .from('knowledge_bases')
        .select(`
          *,
          knowledge_base_domains(domain_id),
          attributions(user_id, ownership_percentage, contribution_type)
        `)
        .eq('id', id)
        .maybeSingle();

      if (error) throw error;
      if (!data) throw new Error('Knowledge base not found');
      
      return data;
    },
    enabled: !!id,
  });

  // Initialize kbData when knowledgeBase is loaded
  useEffect(() => {
    if (knowledgeBase) {
      // Check if current user is the creator
      if (knowledgeBase.creator_id !== user?.id) {
        toast({
          title: "Access Denied",
          description: "You don't have permission to edit this knowledge base.",
          variant: "destructive",
        });
        navigate('/');
        return;
      }

      // Initialize edit data structure
      setKbData({
        id: knowledgeBase.id,
        title: knowledgeBase.title,
        description: knowledgeBase.description,
        domainIds: knowledgeBase.knowledge_base_domains?.map((d: any) => d.domain_id) || [],
        subdomainId: knowledgeBase.subdomain_id,
        keyword: '', // These might not be stored in the current schema
        category: '',
        tags: [],
        notes: '',
        uploadedFiles: knowledgeBase.file_url ? [{
          name: knowledgeBase.file_name,
          url: knowledgeBase.file_url,
          size: knowledgeBase.file_size,
          type: knowledgeBase.file_type,
        }] : [],
        links: [],
        status: knowledgeBase.status,
        is_bounty: knowledgeBase.is_bounty,
        bounty_amount: knowledgeBase.bounty_amount,
      });
    }
  }, [knowledgeBase, user, navigate, toast]);


  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (updatedData: any) => {
      if (!id) throw new Error('No ID provided');

      // Update knowledge base record
      const { error: updateError } = await supabase
        .from('knowledge_bases')
        .update({
          title: updatedData.title,
          description: updatedData.description,
          domain_id: updatedData.domainIds?.[0] || null,
          subdomain_id: updatedData.subdomainId || null,
          file_url: updatedData.uploadedFiles?.[0]?.url || null,
          file_name: updatedData.uploadedFiles?.[0]?.name || null,
          file_size: updatedData.uploadedFiles?.[0]?.size || null,
          file_type: updatedData.uploadedFiles?.[0]?.type || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', id);

      if (updateError) throw updateError;

      // Update domain associations
      // First delete existing associations
      await supabase
        .from('knowledge_base_domains')
        .delete()
        .eq('knowledge_base_id', id);

      // Then insert new ones
      if (updatedData.domainIds && updatedData.domainIds.length > 0) {
        const domainAssociations = updatedData.domainIds.map((domainId: string) => ({
          knowledge_base_id: id,
          domain_id: domainId,
        }));

        await supabase
          .from('knowledge_base_domains')
          .insert(domainAssociations);
      }

      return updatedData;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['knowledge_bases'] });
      queryClient.invalidateQueries({ queryKey: ['knowledge-base', id] });
      toast({
        title: "Knowledge Base Updated",
        description: "Your changes have been saved successfully.",
      });
      if (profile?.username) {
        navigate(`/myspai-profile/${profile.username}`);
      } else {
        navigate('/myspai-profile');
      }
    },
    onError: (error: any) => {
      toast({
        title: "Update Failed",
        description: error.message || "Failed to update knowledge base. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep((currentStep + 1) as EditStep);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((currentStep - 1) as EditStep);
    } else {
      navigate(-1);
    }
  };

  const handleUpdateKbData = (updates: any) => {
    setKbData((prev: any) => ({ ...prev, ...updates }));
  };

  const handleSubmit = () => {
    updateMutation.mutate(kbData);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto">
            <Card className="border-2 border-black">
              <CardContent className="p-8 flex justify-center items-center">
                <Loader2 className="h-8 w-8 animate-spin" />
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  if (error || !knowledgeBase) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <main className="container mx-auto px-4 py-8">
          <div className="max-w-3xl mx-auto">
            <Card className="border-2 border-black">
              <CardContent className="p-8 text-center">
                <p className="text-red-600 mb-4">Failed to load knowledge base.</p>
                <Button onClick={() => navigate(-1)}>Go Back</Button>
              </CardContent>
            </Card>
          </div>
        </main>
      </div>
    );
  }

  if (!kbData) {
    return null;
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        <div className="max-w-3xl mx-auto">
          <div className="mb-8">
            <h1 className="text-3xl font-serif font-bold text-black mb-2">
              Edit Knowledge Base
            </h1>
            <p className="text-gray-600">Editing: {knowledgeBase.title}</p>
          </div>

          {currentStep === 1 && (
            <EditBasicInfo
              kbData={kbData}
              onUpdate={handleUpdateKbData}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 2 && (
            <EditDetails
              kbData={kbData}
              onUpdate={handleUpdateKbData}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 3 && (
            <EditDocuments
              kbData={kbData}
              onUpdate={handleUpdateKbData}
              onNext={handleNext}
              onBack={handleBack}
            />
          )}

          {currentStep === 4 && (
            <EditConfirmation
              kbData={kbData}
              onSubmit={handleSubmit}
              onBack={handleBack}
              isSubmitting={updateMutation.isPending}
            />
          )}
        </div>
      </main>
    </div>
  );
};

export default EditKnowledgeBase;
